/**
 * AddAssignmentDateRange.tsx
 * Date-range picker sub-component for the Add Assignment dialog.
 *
 * Phase 3, Item 5 — stat-holiday-aware date selection.
 * Wave 3 (percent retirement, Q2A): the percent machinery (auto-computed %
 * chip for Vacation/Sick and the manual % input for FieldWork/Other) is GONE —
 * ALL absence types are whole-day on their dated span. The component keeps the
 * working-day count readout and the stat-holiday chips.
 *
 * Props are intentionally simple — the parent dialog owns all state.
 * This component is purely presentational + calls onChange callbacks.
 */

import React from 'react';
import {
  Box,
  TextField,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useTheme } from '@mui/material/styles';
import type { HolidayEntry } from '../../../services/scheduleEmployeeSchedule';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateRangeValue {
  startDate: string; // 'YYYY-MM-DD'
  endDate: string;   // 'YYYY-MM-DD'
}

export interface AddAssignmentDateRangeProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;

  /** Holidays returned from GET /schedule/working-days */
  holidaysInRange: HolidayEntry[];
  /** Working-day count returned from GET /schedule/working-days (null = hidden). */
  workingDayCount: number | null;
  /** True while working-days is being fetched */
  loadingDays: boolean;

  /** Optional label override for Start Date */
  startLabel?: string;
  /** Optional label override for End Date */
  endLabel?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format 'YYYY-MM-DD' as 'Mon D, YYYY' for display. */
function formatHolidayDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-CA', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const AddAssignmentDateRange: React.FC<AddAssignmentDateRangeProps> = ({
  value,
  onChange,
  holidaysInRange,
  workingDayCount,
  loadingDays,
  startLabel = 'Start Date',
  endLabel = 'End Date',
}) => {
  const theme = useTheme();

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value;
    onChange({
      startDate: next,
      endDate: value.endDate < next ? next : value.endDate,
    });
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, endDate: e.target.value });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* ── Date inputs ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
            {startLabel}
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={value.startDate}
            onChange={handleStartChange}
            inputProps={{ 'aria-label': startLabel }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
            {endLabel}
          </Typography>
          <TextField
            type="date"
            size="small"
            fullWidth
            value={value.endDate}
            onChange={handleEndChange}
            inputProps={{
              min: value.startDate,
              'aria-label': endLabel,
            }}
          />
        </Box>
      </Box>

      {/* ── Working-day count (whole days off — Q2A) ─────────────────── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {loadingDays ? (
          <CircularProgress size={14} thickness={4} />
        ) : (
          workingDayCount !== null && (
            <Typography variant="caption" color="text.secondary">
              {`${workingDayCount} working day${workingDayCount !== 1 ? 's' : ''} off`}
            </Typography>
          )
        )}
      </Box>

      {/* ── Stat holidays in range ──────────────────────────────────── */}
      {!loadingDays && holidaysInRange.length > 0 && (
        <Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mb: 0.75,
            }}
          >
            <EventBusyIcon
              sx={{ fontSize: '0.85rem', color: theme.schedule.conflictSoft }}
            />
            <Typography
              variant="caption"
              sx={{ fontSize: '0.72rem', color: theme.schedule.conflictSoft, fontWeight: 600 }}
            >
              Stat holidays in this range (not working days anyway):
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {holidaysInRange.map((h) => (
              <Chip
                key={h.date}
                label={`${formatHolidayDate(h.date)} — ${h.name}`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: '0.65rem',
                  backgroundColor: `${theme.schedule.noActivity}22`,
                  color: theme.palette.text.secondary,
                  border: `1px solid ${theme.border.color}`,
                  opacity: 0.8,
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};
